import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

export default function AddDepartmentModal({
  open,
  onClose,
  onSubmit,
  departmentName,
  setDepartmentName,
  departmentError,
  departmentSuccess,
}) {
  const handleClose = () => {
    onClose();
    setDepartmentName('');
  };
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-department-title"
      aria-describedby="add-department-description"
    >
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          minWidth: 400,
          maxWidth: 600,
          width: '90%',
          mx: 'auto',
          mt: '10vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
            p: 3,
            color: 'white',
            position: 'relative',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="add-department-title" variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 0.5 }}>
            {t('jobDescription.form.addDepartment')}
          </Typography>
          <Typography id="add-department-description" variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            {t('jobDescription.form.addDepartmentDescription','Fill in the department details')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={onSubmit} sx={{ p: 4 }}>
          <TextField
            fullWidth
            required
            id="department-name"
            label={t('jobDescription.form.departmentName','Department Name')}
            placeholder={t('jobDescription.form.departmentNamePlaceholder','Enter department name')}
            value={departmentName}
            onChange={(e) => setDepartmentName(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#0ea5e9',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0ea5e9',
                  borderWidth: 2,
                },
              },
            }}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
            <Button 
              variant="outlined" 
              onClick={handleClose} 
              sx={{ 
                minWidth: 120,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                borderWidth: 2,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ 
                minWidth: 120,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)',
                  boxShadow: 6,
                },
              }}
            >
              {t('jobDescription.form.addDepartment')}
            </Button>
          </Stack>

          {departmentError && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
              {departmentError}
            </Alert>
          )}
          {departmentSuccess && (
            <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
              {departmentSuccess}
            </Alert>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
